import { type ReactNode, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { popIn } from '@shared/theme/animations';
import { IconX } from './icons';

const backdropFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.z.modal};
  padding: ${({ theme }) => theme.spacing(4)};
  animation: ${backdropFade} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};
`;

const Dialog = styled.div`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => theme.spacing(7)};
  /* min(480px, 100%) === 480px на любом вьюпорте ≥ 480; ниже — ужимается до
     ширины бэкдропа, чтобы на узких экранах (360px) не было горизонтального
     скролла. Поведение на десктопе не меняется. */
  min-width: min(480px, 100%);
  max-width: 720px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${popIn} ${({ theme }) => theme.motion.slow} ${({ theme }) => theme.motion.spring};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -4px -4px -4px 0;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export function Modal({ open, title, onClose, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <Backdrop onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <Header>
          <Title>{title}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Закрыть" title="Закрыть">
            <IconX size={18} />
          </CloseBtn>
        </Header>
        {children}
      </Dialog>
    </Backdrop>
  );
}
